// This component provides following controls in Gauge charts
// 	- Start/End angles
// 	- Enable/Disable tick
// 	- Tick size & padding, label padding

import { TextField } from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  updateGaugeAxisOptions,
  updatePieAxisOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartConAxisOptions } from '../../../redux/ChartPoperties/ChartControlsInterface';
import { ChartOptionsProps, ChartOptionsStateProps } from '../CommonInterfaceForChartOptions';
import SliderWithInput from '../SliderWithInput';
import SwitchWithInput from '../SwitchWithInput';
import { handleKeyDown } from '../../CommonFunctions/CommonFunctions';
import GaugeLabelFormat from '../Format/GaugeAxisLabelFormat';

const textFieldStyleProps = {
  style: {
    fontSize: '12px',
    width: '96%',
    margin: '0 auto 0.5rem auto',
    //  backgroundColor: 'white',
    height: '1.5rem',
    paddingLeft: 'px',
    // color: '#404040',
  },
};

interface GridControlsProps {
  updateGaugeAxisOptions: (propKey: string, option: string, value: any) => void;
  updatePieAxisOptions: (propKey: string, option: string, value: any) => void;
  updateFormatOption: (propKey: string, formatKey: string, option: string, value: any) => void;
  iconColor?: string;
  backgroundColor?: string;
}

const GridControls = ({
  // state
  chartControls,
  tabTileProps,
  chartProperties,

  // dispatch
  updateGaugeAxisOptions,
  updatePieAxisOptions,
  iconColor,
  backgroundColor,
}: ChartOptionsProps & GridControlsProps) => {
  var propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const tickId = `enableDisable-${propKey}`;
  const axisLabelId = `enableDisableAxisLabel-${propKey}`;

  var property: ChartConAxisOptions = chartControls.properties[propKey].axisOptions;

  const getDegreeInputSx = (value: number | string) => {
    const stringValue = value ? String(value) : '';
    const charCount = value ? String(value).length : 0;
    let calculatedPixel = 14 + charCount * 7;
    if (stringValue.startsWith('-')) {
      calculatedPixel -= 4;
    }
    const dynamicLeftPosition = `${calculatedPixel}px`;

    return {
      '& .MuiOutlinedInput-root': {
        position: 'relative',
        '&::after': {
          content: '"°"',
          position: 'absolute',
          left: dynamicLeftPosition,
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#666',
          pointerEvents: 'none',
          fontSize: '16px',
          fontWeight: '400',
          zIndex: 5,
          transition: 'left 0.1s ease-out',
        },
        '&.Mui-focused::after': {
          opacity: 0.5,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#2bb9bb',
        },
      },
      '& .MuiOutlinedInput-input': {
        padding: '16.5px 14px',
        textAlign: 'left',
      },
      '@supports (-moz-appearance: none)': {
        '& .MuiOutlinedInput-input': {
          padding: '16.5px 14px',
          textAlign: 'left',
        },
      },
    };
  };

  return (
    <div className="optionsInfo">
      {chartProperties.properties[propKey].chartType === 'gauge' ? (
        <React.Fragment>
          <div
            className="optionDescription"
            style={{
              paddingLeft: '0.3rem',
              marginTop: '10px',
              paddingTop: '0',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Start Angle
          </div>
          <TextField
            value={property.gaugeAxisOptions.startAngle}
            variant="outlined"
            type="number"
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, true)}
            onChange={(e) => {
              updateGaugeAxisOptions(propKey, 'startAngle', e.target.value);
            }}
            InputProps={{
              ...textFieldStyleProps,
              sx: {
                color: iconColor ? iconColor : '#404040',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
              },
            }}
            sx={getDegreeInputSx(property.gaugeAxisOptions.startAngle)}
            //  style={{
            // 	width: "98%",
            // 	paddingLeft: "2px"
            //  }}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          {chartProperties.properties[propKey].chartType === 'pie' ||
          chartProperties.properties[propKey].chartType === 'donut' ||
          chartProperties.properties[propKey].chartType === 'rose' ? (
            <React.Fragment>
              <div
                className="optionDescription"
                style={{
                  paddingLeft: '0.3rem',
                  marginTop: '10px',
                  paddingTop: '0',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Start Angle
              </div>

              <TextField
                value={property.pieAxisOptions.pieStartAngle}
                variant="outlined"
                type="number"
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                  handleKeyDown(event, true)
                }
                onChange={(e) => {
                  let value = parseInt(e.target.value);

                  if (value > 360) value = 360;
                  if (value < 0) value = 0;
                  updatePieAxisOptions(propKey, 'pieStartAngle', value);
                }}
                inputProps={{
                  min: 0,
                  max: 360,
                }}
                InputProps={{
                  ...textFieldStyleProps,
                  sx: {
                    color: iconColor ? iconColor : '#404040',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2bb9bb',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'lightgray',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'lightgray',
                    },
                  },
                }}
                sx={getDegreeInputSx(property.pieAxisOptions.pieStartAngle)}
              />

              <div
                className="optionDescription"
                style={{
                  paddingLeft: '0.3rem',
                  marginTop: '0px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Clockwise
              </div>
              <div
                style={{
                  display: 'flex',
                  width: '97%',
                  margin: '0 auto 0.5rem auto',
                  color: '#404040',
                }}
              >
                <div
                  style={{
                    borderRadius: '5px 0 0 5px',
                    cursor: 'pointer',
                    transition: '0.2s',
                    backgroundColor: !property.pieAxisOptions.clockWise ? 'white' : '#E0E0E0',
                  }}
                  className={
                    property.pieAxisOptions.clockWise ? 'radioButtonSelected' : 'radioButton'
                  }
                  onClick={() => {
                    updatePieAxisOptions(propKey, 'clockWise', true);
                  }}
                >
                  ON
                </div>

                <div
                  style={{
                    borderRadius: '0 5px 5px 0',
                    cursor: 'pointer',
                    transition: '0.2s',
                    backgroundColor: property.pieAxisOptions.clockWise ? 'white' : '#E0E0E0',
                  }}
                  className={
                    !property.pieAxisOptions.clockWise ? 'radioButtonSelected' : 'radioButton'
                  }
                  onClick={() => {
                    updatePieAxisOptions(propKey, 'clockWise', false);
                  }}
                >
                  OFF
                </div>
              </div>
            </React.Fragment>
          ) : null}
        </React.Fragment>
      )}

      {chartProperties.properties[propKey].chartType === 'gauge' ? (
        <React.Fragment>
          <div
            className="optionDescription"
            style={{
              paddingLeft: '0.3rem',
              marginTop: '5px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            End Angle
          </div>

          <TextField
            value={property.gaugeAxisOptions.endAngle}
            variant="outlined"
            type="number"
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, true)}
            onChange={(e) => {
              // changing value of end angle
              updateGaugeAxisOptions(propKey, 'endAngle', e.target.value);
            }}
            InputProps={{
              ...textFieldStyleProps,
              sx: {
                color: iconColor ? iconColor : '#404040',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
              },
            }}
            sx={getDegreeInputSx(property.gaugeAxisOptions.endAngle)}
          />

          <div className="optionDescription" style={{ marginLeft: '-3px', marginTop: '5px' }}>
            <input
              type="checkbox"
              style={{ width: '16px', height: '16px' }}
              id={tickId}
              checked={property.gaugeAxisOptions.showTick}
              onChange={() => {
                updateGaugeAxisOptions(propKey, 'showTick', !property.gaugeAxisOptions.showTick);
              }}
            />
            <label
              htmlFor={tickId}
              className="enableDisableLabel"
              style={{
                fontWeight: '400',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Show Tick
            </label>
          </div>
          {property.gaugeAxisOptions.showTick ? (
            <>
              <div
                className="optionDescription"
                style={{
                  marginTop: '5px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Tick Size
              </div>
              <SliderWithInput
                percent={true}
                sliderValue={property.gaugeAxisOptions.tickSize}
                sliderMinMax={{ min: 0, max: 99, step: 1 }}
                changeValue={(value: number) => {
                  updateGaugeAxisOptions(propKey, 'tickSize', value);
                }}
              />
              <div
                className="optionDescription"
                style={{
                  marginTop: '10px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Tick Padding
              </div>
              <SliderWithInput
                percent={true}
                sliderValue={property.gaugeAxisOptions.tickPadding}
                sliderMinMax={{ min: 0, max: 90, step: 1 }}
                changeValue={(value: number) => {
                  updateGaugeAxisOptions(propKey, 'tickPadding', value);
                }}
              />
            </>
          ) : null}

          <div
            style={{
              borderTop: '1px solid rgb(211,211,211)',
              margin: '1.5rem 4% 0.8rem',
            }}
          ></div>
          <div
            className="optionDescription"
            style={{
              marginLeft: '-3px',
              marginTop: '0px',
              display: 'flex',
              alignItems: 'center',
              marginBottom: '5px',
            }}
          >
            <input
              type="checkbox"
              style={{ width: '16px', height: '16px' }}
              id={axisLabelId}
              checked={property.gaugeAxisOptions.showAxisLabel}
              onChange={() => {
                updateGaugeAxisOptions(
                  propKey,
                  'showAxisLabel',
                  !property.gaugeAxisOptions.showAxisLabel,
                );
              }}
            />
            <label
              htmlFor={axisLabelId}
              className="enableDisableLabel"
              style={{
                fontWeight: '400',
                marginTop: '0px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Show Axis Label
            </label>
          </div>

          {property.gaugeAxisOptions.showAxisLabel ? (
            <>
              <div
                className="optionDescription"
                style={{
                  marginTop: '0px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Label Padding
              </div>
              <div style={{ marginBottom: '0.2rem' }}>
                <SliderWithInput
                  percent={true}
                  sliderValue={property.gaugeAxisOptions.labelPadding}
                  sliderMinMax={{ min: 0, max: 90, step: 1 }}
                  changeValue={(value: number) => {
                    updateGaugeAxisOptions(propKey, 'labelPadding', value);
                  }}
                />
              </div>

              <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                <GaugeLabelFormat iconColor={iconColor} backgroundColor={backgroundColor} />
              </div>
            </>
          ) : null}
        </React.Fragment>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: ChartOptionsStateProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    chartProperties: state.chartProperties,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateGaugeAxisOptions: (propKey: string, option: string, value: any) =>
      dispatch(updateGaugeAxisOptions(propKey, option, value)),
    updatePieAxisOptions: (propKey: string, option: string, value: any) =>
      dispatch(updatePieAxisOptions(propKey, option, value)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(GridControls);
